import React, { useMemo, useState, useEffect } from 'react';
import gridStyles from '../../../styles/DashboardV2.module.css';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useListingContext } from '../../../contexts/ListingContext';
import type { ListingSummary } from '../../../types/ListingTypes';

// Shared components
import ComplianceScoreWidget     from './agent/ComplianceScoreWidget';
import ListingsHealthWidget       from './agent/ListingsHealthWidget';
import ActionRequiredWidget       from './agent/ActionRequiredWidget';
import DocumentHealthWidget       from './agent/DocumentHealthWidget';
import ListingPipelineWidget      from './agent/ListingPipelineWidget';
import UpcomingRemindersWidget    from './agent/UpcomingRemindersWidget';
import ExpiredDocumentsWidget     from './agent/ExpiredDocumentsWidget';
import RecentActivityWidget       from './agent/RecentActivityWidget';
import { replace } from 'react-router-dom';

interface AgentDashboardProps {
    userDetails?: any;
}

const AgentDashboard: React.FC<AgentDashboardProps> = ({ userDetails }) => {
    const {
        overallStats,
        userDocuments,
        fetchUserReminders,
        userReminders,
        fetchRecentDocs,
        loading,
        refreshStructuralData
    } = useDocumentContext();
    const { fetchUserListings } = useListingContext();

    const [listings, setListings] = useState<ListingSummary[]>([]);
    const [recentActivities, setRecentActivities] = useState<any[]>([]);
    const { documentTypes } = useDocumentContext();

    useEffect(() => {
        if (!userDetails?.id) return;
        fetchUserListings(userDetails.id).then(setListings).catch(() => {});
        fetchUserReminders(userDetails.id);
        fetchRecentDocs(userDetails.id).then((docs: any) => {
            if (!Array.isArray(docs)) return;
            const activities = docs.map((d: any) => ({
                id: d.id ?? d._id ?? String(Math.random()),
                text: `Uploaded ${d.title ?? d.fileName ?? 'a document'}`,
                createdAt: d.createdAt ?? d.updatedAt ?? new Date().toISOString(),
            }));
            setRecentActivities(activities);
        }).catch(() => {});
    }, [userDetails?.id, fetchUserListings, fetchUserReminders, fetchRecentDocs]);

    // ── Computed values ────────────────────────────────────────
    const now = useMemo(() => new Date(), []);

    const expiringSoonDocs = useMemo(() => {
        const in30 = new Date(now.getTime() + 30 * 86400000);
        return userDocuments.filter(doc => {
            if (!doc.expiryDate) return false;
            const exp = new Date(doc.expiryDate);
            return exp > now && exp <= in30;
        });
    }, [userDocuments, now]);

    useEffect(() => {
        if (documentTypes.length === 0) {
            refreshStructuralData();
        }
    }, [documentTypes.length, refreshStructuralData]);

    const expiredDocs = useMemo(() => {
        return userDocuments
            .filter(doc => doc.expiryDate && new Date(doc.expiryDate) <= now && doc.replaced == false)
            .map(doc => {
                let matchingType = documentTypes.find(type => type.id == doc.typeID);
                return {
                    id:         doc.id ?? doc._id ?? '',
                    name:       doc.title ?? doc.fileName ?? 'Unknown Document',
                    expiryDate: String(doc.expiryDate),
                    type: matchingType
                }
            });
    }, [userDocuments, now, documentTypes]);

    const openListings       = listings.filter(l => l.status === 'Open').length;
    const pendingListings    = listings.filter(l => l.status === 'Pending').length;
    const closedListings     = listings.filter(l => l.status === 'Closed').length;
    const incompleteListings = listings.filter(l => l.status === 'Incomplete').length;
    const saleListings       = listings.filter(l => l.type === 'Sale').length;
    const rentalListings     = listings.filter(l => l.type === 'Rental').length;

    const activeListings  = openListings + pendingListings;
    const totalListings   = listings.length;
    const healthScore     = totalListings === 0
        ? 100
        : ((totalListings - incompleteListings) / totalListings) * 100;

    return (
        <div className={gridStyles.dashboardPage}>
            <div className={gridStyles.dashboardHeader}>
                <h1 className={gridStyles.dashboardTitle}>My Dashboard</h1>
                <p className={gridStyles.dashboardSubtitle}>
                    Your compliance status, listings, and upcoming tasks at a glance
                </p>
            </div>

            <div className={gridStyles.dashboardGrid}>

                {/* ── Row 1 ── */}
                <div className={gridStyles.col3}>
                    <ComplianceScoreWidget
                        completionRate={overallStats.completionRate}
                        completedItems={overallStats.completedItems}
                        totalItems={overallStats.totalItems}
                        loading={loading}
                    />
                </div>

                <div className={gridStyles.col3}>
                    <ListingsHealthWidget
                        activeCount={activeListings}
                        incompleteCount={incompleteListings}
                        healthScore={healthScore}
                        loading={loading}
                    />
                </div>

                <div className={gridStyles.col6}>
                    <ActionRequiredWidget
                        expiringCount={expiringSoonDocs.length}
                        expiredCount={expiredDocs.length}
                        incompleteListings={incompleteListings}
                        draftListings={0}
                        loading={loading}
                    />
                </div>

                {/* ── Row 2 ── */}
                <div className={gridStyles.col6}>
                    <DocumentHealthWidget
                        totalItems={overallStats.totalItems}
                        completedItems={overallStats.completedItems}
                        expiringItems={expiringSoonDocs.length}
                        expiredItems={overallStats.expiredItems}
                        missingItems={overallStats.missingItems}
                        loading={loading}
                    />
                </div>

                <div className={gridStyles.col6}>
                    <ListingPipelineWidget
                        open={openListings}
                        pending={pendingListings}
                        closed={closedListings}
                        incomplete={incompleteListings}
                        sale={saleListings}
                        rental={rentalListings}
                        loading={loading}
                    />
                </div>

                {/* ── Row 3 ── */}
                <div className={gridStyles.col6}>
                    <UpcomingRemindersWidget
                        userReminders={userReminders}
                        loading={loading}
                    />
                </div>

                <div className={gridStyles.col6}>
                    <ExpiredDocumentsWidget
                        documents={expiredDocs}
                        loading={loading}
                    />
                </div>

                {/* ── Row 4 ── */}
                <div className={gridStyles.col12}>
                    <RecentActivityWidget
                        activities={recentActivities}
                        loading={loading}
                    />
                </div>

            </div>
        </div>
    );

};

export default AgentDashboard;